// ========================================
// PROBLEM STORAGE - Excel Local Storage System
// Usa SheetJS (xlsx) y File System Access API
// ========================================

// Estado global del almacenamiento
let excelWorkbook = null;
let fileHandle = null;
let isInitialized = false;

// Mapeo de niveles
const LEVEL_NAMES = {
    'beginner': 'APRENDIZ',
    'advanced': 'AVANZADO',
    'expert': 'EXPERTO'
};

// Mapeo de tipos de problema
const TYPE_NAMES = {
    'sum': 'SUMA (+)',
    'subtraction': 'RESTA (-)'
};

/**
 * Verifica si el almacenamiento está inicializado
 */
function isStorageInitialized() {
    return isInitialized && excelWorkbook !== null;
}

/**
 * Inicializa el almacenamiento Excel
 * Solicita al usuario dónde guardar el archivo y crea la estructura inicial
 */
async function initializeExcelStorage() {
    console.log('📊 [Storage] Inicializando almacenamiento Excel...');

    try {
        // Verificar soporte de File System Access API
        if (!window.showSaveFilePicker) {
            console.warn('⚠️ [Storage] File System Access API no disponible. Usando modo descarga.');
            return initializeExcelStorageFallback();
        }

        // Solicitar al usuario dónde guardar el archivo
        const options = {
            suggestedName: 'problemas_generados.xlsx',
            types: [{
                description: 'Archivo Excel',
                accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
            }]
        };

        fileHandle = await window.showSaveFilePicker(options);
        console.log('✅ [Storage] Ubicación de archivo seleccionada');

        // Crear workbook inicial con todas las hojas de contextos
        excelWorkbook = createInitialWorkbook();

        // Guardar archivo inicial
        await saveWorkbookToFile();

        isInitialized = true;
        console.log('✅ [Storage] Almacenamiento Excel inicializado correctamente');

        return { success: true, mode: 'file-system-api' };

    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('ℹ️ [Storage] Usuario canceló la selección de archivo');
            return { success: false, cancelled: true };
        }

        console.error('❌ [Storage] Error al inicializar:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Inicializa el almacenamiento en modo fallback (sin File System Access API)
 */
function initializeExcelStorageFallback() {
    console.log('📊 [Storage] Inicializando en modo fallback...');

    excelWorkbook = createInitialWorkbook();
    isInitialized = true;

    console.log('⚠️ [Storage] Modo fallback activado. Deberás descargar manualmente el archivo.');
    return { success: true, mode: 'fallback' };
}

/**
 * Trunca el nombre de una hoja a máximo 31 caracteres (límite de Excel)
 */
function truncateSheetName(name) {
    const maxLength = 31;
    if (name.length <= maxLength) {
        return name;
    }
    // Truncar y añadir puntos suspensivos
    return name.substring(0, maxLength - 3) + '...';
}

/**
 * Crea el workbook inicial con todas las hojas de contextos
 */
function createInitialWorkbook() {
    console.log('📝 [Storage] Creando estructura inicial del workbook...');

    const wb = XLSX.utils.book_new();

    // Obtener todos los contextos
    const contexts = CONTEXTS || [];

    // Crear una hoja por cada contexto
    contexts.forEach(context => {
        const fullName = context?.title?.['es-ES'];
        const sheetName = truncateSheetName(fullName);
        const sheet = createContextSheet();

        XLSX.utils.book_append_sheet(wb, sheet, sheetName);

        // Log si se truncó el nombre
        if (fullName !== sheetName) {
            console.log(`📝 [Storage] Hoja "${fullName}" truncada a "${sheetName}"`);
        }
    });

    console.log(`✅ [Storage] Creadas ${contexts.length} hojas de contexto`);
    return wb;
}

/**
 * Crea una hoja vacía con la estructura de columnas
 */
function createContextSheet() {
    // Estructura de la hoja:
    // | Nivel     | SUMA (+) | RESTA (-) | COMPARACIÓN |
    // | APRENDIZ  |          |           |             |
    // | AVANZADO  |          |           |             |
    // | EXPERTO   |          |           |             |

    const data = [
        ['Nivel', 'Tipo de Operación', 'Número', 'Enunciado del Problema']
    ];

    const sheet = XLSX.utils.aoa_to_sheet(data);

    // Ajustar anchos de columna para nueva estructura
    sheet['!cols'] = [
        { wch: 12 },   // Nivel
        { wch: 20 },   // Tipo de Operación
        { wch: 8 },    // Número
        { wch: 100 }   // Enunciado del Problema
    ];

    return sheet;
}

/**
 * Guarda un problema en el Excel
 * @param {string} contextId - ID del contexto (ej: 'cumpleanos')
 * @param {string} problemType - Tipo de problema ('sum', 'subtraction', 'comparison')
 * @param {string} level - Nivel ('beginner', 'advanced', 'expert')
 * @param {string} problemText - Texto del enunciado del problema
 */
async function saveProblemToExcel(contextId, problemType, level, problemText) {
    if (!isStorageInitialized()) {
        console.warn('⚠️ [Storage] Almacenamiento no inicializado. Problema no guardado.');
        return { success: false, error: 'Not initialized' };
    }

    try {
        // Obtener el nombre de la hoja según el contexto
        const context = CONTEXTS.find(c => c.id === contextId);
        if (!context) {
            throw new Error(`Contexto no encontrado: ${contextId}`);
        }

        const fullName = context?.title?.['es-ES'];
        const sheetName = truncateSheetName(fullName);
        const sheet = excelWorkbook.Sheets[sheetName];

        if (!sheet) {
            throw new Error(`Hoja no encontrada: ${sheetName}`);
        }

        // Convertir hoja a array de arrays para manipular filas
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

        // Mapear a nombres legibles
        const levelName = LEVEL_NAMES[level] || level.toUpperCase();
        const typeName = TYPE_NAMES[problemType] || problemType.toUpperCase();

        // Contar problemas existentes para este nivel+tipo
        let problemNumber = 1;
        for (let i = 1; i < data.length; i++) {
            if (data[i][0] === levelName && data[i][1] === typeName) {
                problemNumber++;
            }
        }

        // Añadir nueva fila con el problema
        data.push([levelName, typeName, problemNumber, problemText]);

        // Recrear hoja completa
        const newSheet = XLSX.utils.aoa_to_sheet(data);

        // Mantener anchos de columna
        newSheet['!cols'] = [
            { wch: 12 },   // Nivel
            { wch: 20 },   // Tipo
            { wch: 8 },    // Número
            { wch: 100 }   // Enunciado
        ];

        // Reemplazar hoja en el workbook
        excelWorkbook.Sheets[sheetName] = newSheet;

        // Guardar el archivo actualizado
        if (fileHandle) {
            await saveWorkbookToFile();
        }

        console.log(`✅ [Storage] Problema guardado: ${sheetName} > ${LEVEL_NAMES[level]} > ${TYPE_NAMES[problemType]} #${problemNumber}`);

        return { success: true };

    } catch (error) {
        console.error('❌ [Storage] Error al guardar problema:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Guarda el workbook en el archivo usando File System Access API
 */
async function saveWorkbookToFile() {
    if (!fileHandle) {
        console.warn('⚠️ [Storage] No hay handle de archivo. Usa downloadExcelBackup() para descargar.');
        return;
    }

    try {
        // Convertir workbook a buffer
        const wbout = XLSX.write(excelWorkbook, { bookType: 'xlsx', type: 'array' });

        // Crear writable stream
        const writable = await fileHandle.createWritable();

        // Escribir el contenido
        await writable.write(wbout);

        // Cerrar el stream
        await writable.close();

        console.log('💾 [Storage] Archivo Excel actualizado');

    } catch (error) {
        console.error('❌ [Storage] Error al guardar archivo:', error);
        throw error;
    }
}

/**
 * Descarga manual del archivo Excel (fallback)
 */
function downloadExcelBackup() {
    if (!isStorageInitialized()) {
        console.error('❌ [Storage] Almacenamiento no inicializado');
        return;
    }

    try {
        // Convertir workbook a buffer
        const wbout = XLSX.write(excelWorkbook, { bookType: 'xlsx', type: 'array' });

        // Crear blob
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // Crear enlace de descarga
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'problemas_generados.xlsx';
        a.click();

        // Limpiar
        URL.revokeObjectURL(url);

        console.log('📥 [Storage] Archivo descargado manualmente');

    } catch (error) {
        console.error('❌ [Storage] Error al descargar archivo:', error);
    }
}

/**
 * Obtiene el workbook actual (para uso avanzado)
 */
function getWorkbook() {
    return excelWorkbook;
}

/**
 * Obtiene estadísticas del almacenamiento
 */
function getStorageStats() {
    if (!isStorageInitialized()) {
        return null;
    }

    const stats = {
        totalSheets: 0,
        totalProblems: 0,
        problemsByContext: {}
    };

    const sheetNames = excelWorkbook.SheetNames;
    stats.totalSheets = sheetNames.length;

    sheetNames.forEach(sheetName => {
        const sheet = excelWorkbook.Sheets[sheetName];
        let contextProblems = 0;

        // Contar problemas en cada celda (B2:D4)
        ['B', 'C', 'D'].forEach(col => {
            [2, 3, 4].forEach(row => {
                const cell = sheet[`${col}${row}`];
                if (cell && cell.v) {
                    // Contar separadores para determinar número de problemas
                    const problems = cell.v.split('---').length;
                    contextProblems += problems;
                    stats.totalProblems += problems;
                }
            });
        });

        stats.problemsByContext[sheetName] = contextProblems;
    });

    return stats;
}

// Exportar funciones al objeto window para uso global
if (typeof window !== 'undefined') {
    window.ProblemStorage = {
        initialize: initializeExcelStorage,
        isInitialized: isStorageInitialized,
        saveProblem: saveProblemToExcel,
        downloadBackup: downloadExcelBackup,
        getWorkbook: getWorkbook,
        getStats: getStorageStats
    };
}
